import { createFeatureSelector, createSelector } from '@ngrx/store';
import { OrgUiConfig } from 'shared-general-libs/type/org';
import { UserState } from './user.reducer';
import { APP_STORE } from '@ieCore/consts/store.consts';

export const selectUserState = createFeatureSelector<UserState>(APP_STORE.USER);

export const selectUserError = createSelector(
  selectUserState,
  (state: UserState) => state.error,
);

export const selectUser = createSelector(
  selectUserState,
  (state: UserState) => state.user,
);

export const selectUserId = createSelector(
  selectUserState,
  (state: UserState) => state.user?.id,
);

export const selectUserOrganizationId = createSelector(
  selectUserState,
  (state: UserState) => state.user?.orgId,
);

export const selectOrgCountryCode = createSelector(
  selectUserState,
  (state: UserState) => state.user?.orgCountryCode,
);

/**
 * N1 (Wave 5): the tenant's My Tasks column delta, carried on the session
 * payload (`GET /users/me`) exactly like `orgCountryCode` above — NOT read
 * through `OrganizationService.getOrgConfig()`, which requires
 * `Organization.VIEW` and silently swallows the 403 into `{}`. Measured
 * 2026-08-20: 126 of 188 roles that can open My Tasks lack that permission
 * (incl. MRF's Data Owner/Reviewer roles), so the org-read path would
 * silently ship default columns to exactly N1's audience.
 *
 * Cast note (same precedent as `OrgConfigWithDerivedFlags`): `orgUiConfig`
 * reaches the sgl `UserResponseDto` with the Wave-5 sgl addition; until the
 * installed sgl carries it, the getMe payload simply omits the field
 * (=> undefined => platform-default columns). Do NOT strip the cast.
 */
export const selectMyTasksColumnConfig = createSelector(
  selectUser,
  (user) =>
    (
      user as
        | (NonNullable<UserState['user']> & { orgUiConfig?: OrgUiConfig })
        | null
    )?.orgUiConfig?.myTasksColumns,
);

/**
 * Mirrors sgl's `OrgSessionConfigDto` (`shared-general-libs/dto/org/org-session-config.dto`).
 * Declared locally rather than imported for two reasons: (1) sgl has no plain
 * `type/org` interface for it yet (unlike `OrgUiConfig`/`OrgUiConfigDto`), and
 * (2) this app's sgl is main-tracked — develop-PR CI validates imports against
 * sgl `main`, so a develop-only DTO symbol would fail CI until sgl promotes it
 * (same reason `selectMyTasksColumnConfig` above keeps its cast instead of
 * importing `OrgUiConfigDto`). Confirmed the deep path doesn't even resolve
 * locally today (`tsc` → `TS2307: Cannot find module
 * 'shared-general-libs/dto/org/org-session-config.dto'`, because sgl's
 * `typesVersions` only maps barrel `index.d.ts` files, no wildcard); the
 * barrel `shared-general-libs/dto/org` does resolve, but importing the DTO
 * class from it would still carry the sgl-main-lag CI risk.
 */
export type OrgSessionConfig = {
  wasteDerivedGenerationEnabled?: boolean;
  waterDerivedConsumptionEnabled?: boolean;
  fuelAndPowerEnergyEnabled?: boolean;
  fuelAndPowerGroupingEnabled?: boolean;
  activityFormDefaults?: Record<string, Record<string, string>>;
  idleLogoutMinutes?: number;
};

/**
 * C-2: the tenant's feature flags + per-activity form defaults, carried on the
 * session payload (`GET /users/me`) for the same reason as `orgUiConfig` above.
 * Consumed by OrganizationService.getOrgConfig() (session-first, org-read
 * fallback) through this selector.
 *
 * R5 fix: `orgId` and `orgConfig` bundled into ONE atomic read off `selectUser`.
 *
 * OrganizationService.getOrgConfig() used to `combineLatest` two SEPARATE
 * `store.select()`s (one for orgId, one for orgConfig). Against the REAL
 * store, a single `loadSuccess` dispatch notifies those two independent
 * subscribers sequentially and synchronously, so `combineLatest` re-emits
 * once with the NEW orgId paired with the STALE orgConfig (`undefined` on
 * first login) — `getOrgConfig()`'s switchMap saw that glitch tick and fired
 * a spurious `orgRepo.getCompany` (a 403 on every login for exactly the
 * roles C-2 exists to avoid the org-read for) before the second, correct
 * emission arrived. Reading both fields off a SINGLE selector closes the gap:
 * they change together in the same store emission, so there is no
 * intermediate tick where one is new and the other stale.
 *
 * `createSelector` memoizes on `selectUser`'s output reference — the
 * projected `{ orgId, orgConfig }` object is only rebuilt when the `user`
 * object itself changes, so it stays referentially stable (and therefore
 * `distinctUntilChanged` on its fields is cheap/correct) across unrelated
 * state emissions.
 */
export const selectUserOrgContext = createSelector(selectUser, (user) => ({
  orgId: user?.orgId,
  orgConfig: (
    user as
      | (NonNullable<UserState['user']> & { orgConfig?: OrgSessionConfig })
      | null
  )?.orgConfig,
}));
